/** @prettier */

import {Grid, Card, CardContent, Stack, Box} from '@mui/material';
import {useEffect, useState} from 'react';
import {useLoaderData, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import GroupLayoutSetting from '../../component/groupLayout/GroupLayoutSetting';
import GroupUnitEditDialog from './GroupUnitEditDialog';
import CardContents from '../../component/CardContents';
//API import
import {
  getCompositionsGroupsByGroupID,
  getCompositionsGroups,
  getCompositionsGateway,
  getCompositionsSLUnit,
  getCompositionsSLIFUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
//type import
import {layoutUnitListType} from '../../Type/TypeDef';

import {useOutlet} from '../floor-registration/FloorMain';
import {sortByUnicode} from '../../functions/sort';

type LoaderReturnType = {
  /** グループ情報 */
  groupInfo: apiIF.res.getCompositionsGroupsByGroupID | undefined;
  /** ユニット情報 */
  unitList: layoutUnitListType;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.groupID === undefined) {
    return undefined;
  }

  const promises = [];
  promises.push(getCompositionsGroupsByGroupID(params.siteID, params.groupID));
  promises.push(
    getCompositionsGateway(params.siteID, {
      'fields': 'register-number',
      'max-result': 100,
    })
  );

  try {
    const response = await Promise.all(promises);
    //表示しているグループの情報を取得
    const groupInfo = response[0] as apiIF.res.getCompositionsGroupsByGroupID;
    //ゲートウェイの情報を取得
    const gwInfomations = response[1] as apiIF.res.getCompositionsGateway;
    //表示内容をUnicode順に並び替え
    groupInfo['unit-informations'].sort(sortByUnicode('unit-name'));

    //サイトに登録されているSL、SLIFユニット情報を取得
    const unitList: LoaderReturnType['unitList'] = [];
    const slPromises = [];
    for (const gwInfo of gwInfomations['gateway-informations']) {
      if (
        gwInfo['register-number'] === undefined ||
        (gwInfo['register-number']['sl-system-1']['indoor-unit'] === '0' &&
          gwInfo['register-number']['sl-system-2']['indoor-unit'] === '0')
      ) {
        continue;
      }
      slPromises.push(
        getCompositionsSLUnit(params.siteID, gwInfo['gateway-id'], {
          'fields': 'indoor-unit-informations',
          'max-result': 400,
        })
      );
      slPromises.push(
        getCompositionsSLIFUnit(params.siteID, gwInfo['gateway-id'], {
          'fields': 'unit-type',
          'max-result': 400,
        })
      );
    }
    const slResponse = await Promise.all(slPromises);
    // ユニットIDと室内機、空調機タイプのみを抽出
    slResponse.forEach(unitInfomations => {
      if ('outdoor-unit-informations' in unitInfomations) {
        //SLユニット
        for (let slOU of unitInfomations['outdoor-unit-informations']) {
          if (slOU['indoor-unit-informations'] === undefined) {
            continue;
          }
          for (let slIU of slOU['indoor-unit-informations']) {
            unitList.push({
              unitID: slIU['unit-id'],
              unitType: slIU['indoor-unit-type'],
            });
          }
        }
      } else {
        //SLIFユニット
        for (let slIF of unitInfomations['unit-informations']) {
          if (slIF['unit-type'] === undefined) {
            continue;
          }
          unitList.push({
            unitID: slIF['unit-id'],
            unitType: slIF['unit-type'],
          });
        }
      }
    });

    return {groupInfo: groupInfo, unitList: unitList};
  } catch {
    return undefined;
  }
}

/**
 * グループレイアウト
 * @returns JSX.Element
 */
const GroupLayoutRegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();
  //親コンポーネントから値を取得
  const {allGroupInfo, setAllGroupInfo} = useOutlet();

  const [displayGroupInfo, setDisplayGroupInfo] =
    useState<apiIF.res.getCompositionsGroupsByGroupID | null>(null); //表示しているグループの情報
  const [unitList, setUnitList] = useState<LoaderReturnType['unitList']>([]); //サイトにあるSL、SLIFユニットのリスト

  useEffect(() => {
    if (loaderData === undefined || loaderData['groupInfo'] === undefined) {
      return;
    }
    setDisplayGroupInfo(loaderData['groupInfo']);
    setUnitList(loaderData['unitList']);
  }, [loaderData]);

  //リストから室内機の登録が変更されたとき、ツリービューの表示を更新する
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //サイトに登録されているすべてのグループの情報を取得
    getCompositionsGroups(siteID, {'max-result': 400}).then(response => {
      //表示内容をUnicode順に並び替え
      response['group-informations'].sort(sortByUnicode('group-name'));
      for (const info of response['group-informations']) {
        if (info['unit-informations'] === undefined) continue;
        info['unit-informations'].sort(sortByUnicode('unit-name'));
      }
      setAllGroupInfo(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayGroupInfo, siteID]);

  //グループの情報を取得できない場合
  if (displayGroupInfo === null) {
    return <></>;
  }
  return (
    <>
      {/* グループ名称の表示 */}
      <Box className="groupSetting__title">
        {displayGroupInfo['group-name']}
      </Box>
      <Stack spacing={4}>
        {/* グループレイアウト表示 */}
        <GroupLayoutSetting
          displayGroupInfo={displayGroupInfo}
          setDisplayGroupInfo={setDisplayGroupInfo}
          unitList={unitList}
        />
        {/* 室内機リスト表示 */}
        <Card className="groupSetting__contents--layout">
          <CardContent>
            <Stack spacing={2}>
              {/* 登録室内機編集ボタン */}
              <Box>
                <Grid
                  container
                  justifyContent="space-between"
                  className="groupSetting__contents__title">
                  <Grid item xs={2}>
                    {trans('GroupLayoutRegistration.title')}
                  </Grid>
                  <Grid item xs="auto">
                    <GroupUnitEditDialog
                      allGroupInfo={allGroupInfo}
                      displayGroupInfo={displayGroupInfo}
                      setDisplayGroupInfo={setDisplayGroupInfo}
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* 室内機登録台数と名前 */}
              <Box>
                <Stack spacing={2} className="groupSetting__contents__contents">
                  {/* 室内機登録台数 */}
                  <CardContents
                    class="groupSetting__contents__contents__underLine"
                    valueDescription={trans(
                      'GroupLayoutRegistration.item.title.numberOfIndoorUnits'
                    )}
                    value={String(displayGroupInfo['unit-informations'].length)}
                  />
                  {/* 登録室内機名称 */}
                  <CardContents
                    class="groupSetting__contents__contents__underLine"
                    valueDescription={trans(
                      'GroupLayoutRegistration.item.title.indoorUnit'
                    )}
                    value={
                      <Stack spacing={0}>
                        {displayGroupInfo['unit-informations'].map(
                          groupUnit => (
                            <Box key={groupUnit['unit-id']}>
                              {groupUnit['unit-name']}
                            </Box>
                          )
                        )}
                      </Stack>
                    }
                  />
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};
export default GroupLayoutRegistration;
